// Proposing an assistance to ONE account of a pharmacy (contract 0.6.0, PRD-2026-0021). Three steps in one dialog: the
// pharmacy (search), the account (name and role, « vu il y a… » as a hint, never a promise of presence), the reason (shown to
// the person, so no patient data). The pharmacy sees nothing before it accepts, and no message leaves the application.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { assistError, assistSessionPath, lastSeenText, notifyQueueChanged } from "../../lib/assist";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

const REASON_MIN = 5;
const REASON_MAX = 500;

export default function InviteDialog({ open, onOpenChange, initialPharmacy = "", initialReason = "" }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState(initialPharmacy);
  const [pharmacies, setPharmacies] = useState({ phase: "idle", items: [], error: "" });
  const [pharmacy, setPharmacy] = useState(null);
  const [accounts, setAccounts] = useState({ phase: "idle", items: [], error: "" });
  const [account, setAccount] = useState(null);
  const [reason, setReason] = useState(initialReason);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    setPharmacy(null); setAccount(null); setError(""); setReason(initialReason); setSearch(initialPharmacy);
    return undefined;
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || pharmacy) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setPharmacies((s) => ({ ...s, phase: "loading" }));
      try {
        const { data } = await api.get("/assist/pharmacies", { params: { search: search.trim() || undefined } });
        if (cancelled) return;
        setPharmacies({ phase: "ready", items: data.items || [], error: "" });
        // Coming from a ticket: the pharmacy named there is picked as soon as it is the only match.
        if (initialPharmacy && search === initialPharmacy && (data.items || []).length === 1) setPharmacy(data.items[0]);
      } catch (e) {
        if (!cancelled) setPharmacies({ phase: "error", items: [], error: assistError(e).message });
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [open, pharmacy, search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!pharmacy) { setAccounts({ phase: "idle", items: [], error: "" }); return undefined; }
    let cancelled = false;
    setAccounts({ phase: "loading", items: [], error: "" });
    api.get(`/assist/pharmacies/${encodeURIComponent(pharmacy.ref)}/accounts`)
      .then(({ data }) => { if (!cancelled) setAccounts({ phase: "ready", items: data || [], error: "" }); })
      .catch((e) => { if (!cancelled) setAccounts({ phase: "error", items: [], error: assistError(e).message }); });
    return () => { cancelled = true; };
  }, [pharmacy]);

  const reasonLength = reason.trim().length;
  const reasonOk = reasonLength >= REASON_MIN && reasonLength <= REASON_MAX;
  const blocked = pharmacy && pharmacy.invitations_enabled === false;
  const canSend = !!pharmacy && !!account && reasonOk && !blocked && !sending;
  const now = useMemo(() => Date.now(), [accounts.items]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (event) => {
    event.preventDefault();
    if (!canSend) return;
    setSending(true); setError("");
    try {
      const { data } = await api.post("/assist/invitations", { pharmacy: pharmacy.ref, account: account.ref, reason: reason.trim() });
      toast.success(`Proposition envoyée à ${account.name}`);
      notifyQueueChanged();
      onOpenChange(false);
      navigate(assistSessionPath(data.id));
    } catch (e) {
      setError(assistError(e).message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="assist-invite-dialog">
        <DialogHeader>
          <DialogTitle>Lancer une assistance</DialogTitle>
          <DialogDescription>
            Proposez votre aide à une personne de la pharmacie. Elle voit votre nom et votre motif dans son application, et choisit
            d'accepter ou de refuser : rien n'est partagé sans elle.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={send} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="invite-pharmacy">Pharmacie</Label>
            {pharmacy ? (
              <div className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="invite-pharmacy-picked">
                <span className="min-w-0 truncate font-medium text-slate-900">{pharmacy.name}{pharmacy.region ? <span className="font-normal text-slate-500"> · {pharmacy.region}</span> : null}</span>
                <Button type="button" variant="ghost" size="sm" onClick={() => { setPharmacy(null); setAccount(null); }} data-testid="invite-pharmacy-change">Changer</Button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                  <Input id="invite-pharmacy" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nom de la pharmacie…"
                    className="pl-9" autoComplete="off" data-testid="invite-pharmacy-search" />
                </div>
                <div className="max-h-44 overflow-y-auto rounded-md border border-slate-200" data-testid="invite-pharmacy-list">
                  {pharmacies.phase === "loading" && <p className="p-3 text-sm text-slate-500"><Loader2 size={14} className="mr-1.5 inline animate-spin" />Recherche…</p>}
                  {pharmacies.phase === "error" && <p className="p-3 text-sm text-red-700" role="alert">{pharmacies.error}</p>}
                  {pharmacies.phase === "ready" && pharmacies.items.length === 0 && <p className="p-3 text-sm text-slate-500" data-testid="invite-pharmacy-empty">Aucune pharmacie ne correspond.</p>}
                  {pharmacies.items.map((item) => (
                    <button key={item.ref} type="button" onClick={() => setPharmacy(item)} data-testid="invite-pharmacy-option"
                      className="flex w-full items-center justify-between gap-2 border-b border-slate-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-slate-50">
                      <span className="min-w-0 truncate">{item.name}{item.region ? <span className="text-slate-500"> · {item.region}</span> : null}</span>
                      {item.invitations_enabled === false && <span className="shrink-0 text-2xs text-amber-700">n'accepte pas les propositions</span>}
                    </button>
                  ))}
                </div>
              </>
            )}
            {blocked && <p className="text-sm text-amber-700" role="status" data-testid="invite-blocked">Cette pharmacie n'accepte pas les propositions de l'équipe Jamora.</p>}
          </div>

          {pharmacy && !blocked && (
            <fieldset className="space-y-1.5">
              <legend className="text-sm font-medium text-slate-900">Personne à contacter</legend>
              {accounts.phase === "loading" && <p className="text-sm text-slate-500"><Loader2 size={14} className="mr-1.5 inline animate-spin" />Chargement des comptes…</p>}
              {accounts.phase === "error" && <p className="text-sm text-red-700" role="alert">{accounts.error}</p>}
              {accounts.phase === "ready" && accounts.items.length === 0 && <p className="text-sm text-slate-500" data-testid="invite-accounts-empty">Aucun compte de cette pharmacie ne peut recevoir d'assistance.</p>}
              <div className="space-y-1" role="radiogroup" aria-label="Personne à contacter" data-testid="invite-accounts">
                {accounts.items.map((item) => (
                  <label key={item.ref} className={`flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2 text-sm ${account?.ref === item.ref ? "border-jamora bg-jamora/5" : "border-slate-200 hover:bg-slate-50"}`}
                    data-testid="invite-account-option">
                    <input type="radio" name="invite-account" checked={account?.ref === item.ref} onChange={() => setAccount(item)} className="h-4 w-4" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-slate-900">{item.name}</span>
                      <span className="block truncate text-xs text-slate-500">{item.role} · {lastSeenText(item.last_seen_at, now)}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          {pharmacy && !blocked && (
            <div className="space-y-1.5">
              <Label htmlFor="invite-reason">Motif (montré à la personne)</Label>
              <Textarea id="invite-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={REASON_MAX}
                placeholder="Ex. : votre imprimante de tickets ne répond plus, je peux regarder avec vous ?" data-testid="invite-reason" />
              <p className="text-xs text-slate-500">N'indiquez aucun nom de patient ni donnée de santé. ({reasonLength}/{REASON_MAX})</p>
            </div>
          )}

          {error && <p className="text-sm text-red-700" role="alert" data-testid="invite-error">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={!canSend} data-testid="invite-send">
              {sending && <Loader2 size={14} className="mr-1.5 animate-spin" />}Envoyer la proposition
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
